import { tokenize } from "@common/utils/string";
import {
  HEALTH_HEALTHY,
  HEALTH_PROVISIONING,
  HEALTH_UNHEALTHY,
  MIGRATION_STATUS_CANCELLED,
  MIGRATION_STATUS_COMPLETED,
  MIGRATION_STATUS_INITIALIZED,
  MIGRATION_STATUS_MIGRATING,
  MIGRATION_STATUS_SNAPSHOT_UPLOADED,
  STATUS_CHANGE_REGION,
  STATUS_CHANGING_SUBSCRIPTION_PLAN,
  STATUS_CREATING,
  STATUS_DELETING,
  STATUS_STARTING,
  STATUS_STOPPING,
  STATUS_SUSPENDED,
  STATUS_SUSPENDING,
  STATUS_UPDATING_VERSION,
} from "./constants";
import {
  HealthType,
  IInstance,
  MigrationStatusType,
  StatusType,
} from "./types";

export function getInstanceDNSAlias(base: string): string {
  return `${tokenize(base)}-${Math.floor(Math.random() * 100)}`;
}

export function formatInstanceVersion(
  version: string,
  compact = false,
): string {
  const versionSplit = version.split("/");
  const versionShortName = versionSplit[versionSplit.length - 1];
  const theVersion = versionShortName.split(":").join(" ");
  if (compact) {
    const theCompactVersion = theVersion.split(" ").pop();
    if (theCompactVersion) {
      const [theSimplifiedVersion] = theCompactVersion.split("-");
      return theSimplifiedVersion;
    }

    throw new Error(`formatInstanceVersion, compact: ${version}`);
  }

  return theVersion;
}

export function getPrettySelfHostedInstanceName(isEnterprise: boolean): string {
  if (isEnterprise) {
    return "Self-hosted enterprise instance";
  }

  return "Self-hosted instance";
}

type GetInstanceUrlProps = Pick<IInstance, "alias_url" | "dns_alias">;

export function getInstancePathname({
  alias_url,
  dns_alias,
}: GetInstanceUrlProps): string {
  return alias_url || `${dns_alias}.metabaseapp.com`;
}

export function getInstanceUrl({
  alias_url,
  dns_alias,
}: GetInstanceUrlProps): string {
  const pathname = getInstancePathname({ alias_url, dns_alias });
  const protocol = pathname.indexOf("http") === 0 ? "" : "https://";
  return `${protocol}${pathname}`;
}

export function getMigrationUrl(uploadS3Key: string): string | null {
  if (process.env.NEXT_PUBLIC_API_BASE_URL) {
    const baseAPIUrl: URL = new URL(process.env.NEXT_PUBLIC_API_BASE_URL);
    const { protocol, hostname, port } = baseAPIUrl;
    return `${protocol}//${hostname}${
      port ? `:${port}` : ""
    }/migrate/${uploadS3Key}`;
  }

  return null;
}

export function isChangingHealth({
  health,
}: Pick<IInstance, "health">): boolean {
  return health === HEALTH_PROVISIONING || health === HEALTH_UNHEALTHY;
}

export function isChangingStatus({
  status,
}: Pick<IInstance, "status">): boolean {
  return (
    status === STATUS_CREATING ||
    status === STATUS_STARTING ||
    status === STATUS_UPDATING_VERSION ||
    status === STATUS_CHANGING_SUBSCRIPTION_PLAN ||
    status === STATUS_CHANGE_REGION ||
    status === STATUS_STOPPING ||
    status === STATUS_SUSPENDING ||
    status === STATUS_DELETING
  );
}

export function isChanging({
  is_deleted,
  health,
  status,
  migration_status,
}: Pick<
  IInstance,
  "is_deleted" | "health" | "status" | "migration_status"
>): boolean {
  return (
    // If a customer has not paid, the instance is suspended,
    // so we show its status
    status !== STATUS_SUSPENDED &&
    !is_deleted &&
    (isChangingHealth({ health }) ||
      isChangingStatus({ status }) ||
      isMigrationInProgress({ health, migration_status }))
  );
}

export function isMigrationInProgress({
  health,
  migration_status,
}: Pick<IInstance, "health" | "migration_status">): boolean {
  return (
    migration_status === MIGRATION_STATUS_INITIALIZED ||
    migration_status === MIGRATION_STATUS_SNAPSHOT_UPLOADED ||
    migration_status === MIGRATION_STATUS_MIGRATING ||
    (health === HEALTH_UNHEALTHY &&
      migration_status === MIGRATION_STATUS_COMPLETED)
  );
}

export function isMigrationCancelled({
  migration_status,
}: Pick<IInstance, "migration_status">): boolean {
  return migration_status === MIGRATION_STATUS_CANCELLED;
}

export function isMigrationCompleted({
  health,
  migration_status,
}: Pick<IInstance, "health" | "migration_status">): boolean {
  return (
    health === HEALTH_HEALTHY && migration_status === MIGRATION_STATUS_COMPLETED
  );
}

export function getInstanceStatus(
  hosted_instance: IInstance,
): MigrationStatusType | StatusType | HealthType {
  const { migration_status, status, health } = hosted_instance;

  return migration_status && isMigrationInProgress(hosted_instance)
    ? migration_status
    : isChangingStatus(hosted_instance) ||
        // If a customer has not paid, the instance is suspended,
        // so we show its status
        hosted_instance.status === STATUS_SUSPENDED
      ? status
      : health;
}
